import { SCALE_LIBRARY, type ScaleDefinition } from './curriculum'

export const ABSOLUTE_PITCH_SCALE_IDS = [
  'ionian',
  'aeolian',
  'harmonic-minor',
  'melodic-minor',
  'dorian',
  'phrygian',
  'lydian',
  'mixolydian',
  'locrian',
] as const

export const PITCH_RAIL_LABELS = [
  'C',
  'C♯/D♭',
  'D',
  'D♯/E♭',
  'E',
  'F',
  'F♯/G♭',
  'G',
  'G♯/A♭',
  'A',
  'A♯/B♭',
  'B',
] as const

const TONIC_NAMES = ['C', 'D♭', 'D', 'E♭', 'E', 'F', 'F♯', 'G', 'A♭', 'A', 'B♭', 'B'] as const
const LETTERS = ['C', 'D', 'E', 'F', 'G', 'A', 'B'] as const
const NATURAL_PITCH_CLASSES: Record<(typeof LETTERS)[number], number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 }

export type ScaleContext = {
  tonic: number
  tonicName: string
  scale: ScaleDefinition
  noteNames: string[]
  pitchClasses: number[]
}

export function absolutePitchScales(): ScaleDefinition[] {
  return ABSOLUTE_PITCH_SCALE_IDS.map((id) => SCALE_LIBRARY.find((scale) => scale.id === id)).filter((scale): scale is ScaleDefinition => Boolean(scale))
}

export function scaleDisplayName(scale: ScaleDefinition): string {
  if (scale.id === 'ionian') return 'Major'
  if (scale.id === 'aeolian') return 'Natural Minor'
  return scale.name
}

export function tonicNameForPitchClass(pitchClass: number): string {
  return TONIC_NAMES[((pitchClass % 12) + 12) % 12]
}

function accidentalFor(delta: number): string {
  if (delta === -2) return '♭♭'
  if (delta === -1) return '♭'
  if (delta === 1) return '♯'
  if (delta === 2) return '♯♯'
  return ''
}

export function createScaleContext(tonic: number, scaleId: string): ScaleContext {
  const scale = absolutePitchScales().find((candidate) => candidate.id === scaleId) ?? absolutePitchScales()[0]
  const normalizedTonic = ((tonic % 12) + 12) % 12
  const tonicName = tonicNameForPitchClass(normalizedTonic)
  const tonicLetterIndex = LETTERS.indexOf(tonicName[0] as (typeof LETTERS)[number])
  const pitchClasses = scale.intervals.map((interval) => (normalizedTonic + interval) % 12)
  const noteNames = pitchClasses.map((pitchClass, degree) => {
    const letter = LETTERS[(tonicLetterIndex + degree) % LETTERS.length]
    const natural = NATURAL_PITCH_CLASSES[letter]
    let delta = pitchClass - natural
    while (delta > 6) delta -= 12
    while (delta < -6) delta += 12
    return `${letter}${accidentalFor(delta)}`
  })
  return { tonic: normalizedTonic, tonicName, scale, noteNames, pitchClasses }
}

export function pitchNameInContext(context: ScaleContext, pitchClass: number): string | undefined {
  const index = context.pitchClasses.indexOf(((pitchClass % 12) + 12) % 12)
  return index >= 0 ? context.noteNames[index] : undefined
}
